import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircle2, MessageSquare, PauseCircle, Send, TriangleAlert } from 'lucide-react';
import {
  CommunicationDeliveryMode,
  CommunicationPurpose,
  CustomerCommunicationStatus,
  PERMISSIONS,
  type CustomerCommunicationSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { StatCard } from '@/components/ui/StatCard';
import { useBranch } from '@/hooks/useBranch';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useCommunicationSummaryQuery,
  useCommunicationsQuery,
  type CommunicationListParams,
} from '../api/crm.api';
import { CommunicationDetailDrawer } from '../components/CommunicationDetailDrawer';
import { CommunicationStatusBadge } from '../components/communicationLabels';
import { OperationalHealth } from '../components/OperationalHealth';
import { RemindersPanel } from '../components/RemindersPanel';
import { BirthdaysPanel } from '../components/BirthdaysPanel';

const PAGE_SIZE = 20;

type Tab = 'messages' | 'reminders' | 'birthdays';

/**
 * What the shop has told its customers, and what it is about to.
 *
 * The counts and the table take the same filters, so the summary always describes what is on
 * screen rather than some wider truth. There is deliberately no "delivered" or "read" tile: the
 * platform cannot prove either, and the one place an unprovable claim does real damage is a
 * dashboard somebody quotes to a customer.
 *
 * No filter reaches the address bar. A message's words and a customer's number are the two things
 * that must not sit in a URL, and the simplest guarantee is that this page has no text search at all.
 */
export function CommunicationsPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { branches, activeBranch } = useBranch();

  const [tab, setTab] = useState<Tab>('messages');
  const [branchId, setBranchId] = useState('');
  const [purpose, setPurpose] = useState<CommunicationPurpose | ''>('');
  const [status, setStatus] = useState<CustomerCommunicationStatus | ''>('');
  const [deliveryMode, setDeliveryMode] = useState<CommunicationDeliveryMode | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const onFilter =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const params: CommunicationListParams = {
    page,
    pageSize: PAGE_SIZE,
    branchId,
    purpose,
    status,
    deliveryMode,
    dateFrom,
    dateTo,
  };

  const { data, isPending, isError, error, refetch } = useCommunicationsQuery(params);
  const summary = useCommunicationSummaryQuery(params);

  const columns: Column<CustomerCommunicationSummary>[] = [
    {
      key: 'customer',
      header: t('crm.fields.customer'),
      primary: true,
      render: (row) => (
        <Link className="text-brand underline" to={`/customers/${row.customerId}`}>
          <span dir="auto">{row.customerName}</span>
        </Link>
      ),
    },
    {
      key: 'preparedAt',
      header: t('crm.fields.date'),
      render: (row) => <span dir="ltr">{new Date(row.preparedAt).toLocaleString()}</span>,
    },
    {
      key: 'branch',
      header: t('crm.fields.branch'),
      render: (row) => <span dir="auto">{row.branchName}</span>,
    },
    {
      key: 'purpose',
      header: t('crm.fields.purpose'),
      render: (row) => t(`crm.purpose.${row.purpose}`),
    },
    {
      key: 'deliveryMode',
      header: t('crm.fields.deliveryMode'),
      hideOnCard: true,
      render: (row) => (
        <Badge tone={row.deliveryMode === 'AUTOMATED_PROVIDER' ? 'brand' : 'neutral'}>
          {t(`crm.deliveryMode.${row.deliveryMode}`)}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: t('crm.fields.status'),
      render: (row) => <CommunicationStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedId(row.id)}
          aria-label={t('crm.openNamed', { customer: row.customerName })}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  const branchOptions = [
    { value: '', label: t('crm.filters.allBranches') },
    ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={t('crm.title')} subtitle={t('crm.subtitle')} />

      <OperationalHealth branchId={activeBranch?.id ?? branches[0]?.id ?? null} />

      <div className="flex flex-wrap gap-2">
        {(['messages', 'reminders', 'birthdays'] as const).map((option) => (
          <Button
            key={option}
            size="sm"
            variant={tab === option ? 'primary' : 'secondary'}
            onClick={() => setTab(option)}
          >
            {t(`crm.tabs.${option}`)}
          </Button>
        ))}
      </div>

      {tab === 'messages' ? (
        <>
          {/* `min-w-0` on the children: a grid item defaults to `min-width: auto`, so the longest
              hint sized the track past the viewport and the whole page scrolled sideways at 375px. */}
          <div className="grid gap-4 [&>*]:min-w-0 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              icon={PauseCircle}
              tone="neutral"
              label={t('crm.counts.pending')}
              value={summary.data?.pending ?? 0}
              hint={t('crm.counts.pendingHint')}
              isLoading={summary.isPending}
            />
            {/* Brand, not success: accepted by WhatsApp is not received by a customer. */}
            <StatCard
              icon={Send}
              tone="brand"
              label={t('crm.counts.providerAccepted')}
              value={summary.data?.providerAccepted ?? 0}
              hint={t('crm.counts.providerAcceptedHint')}
              isLoading={summary.isPending}
            />
            <StatCard
              icon={CheckCircle2}
              tone="neutral"
              label={t('crm.counts.suppressed')}
              value={summary.data?.suppressed ?? 0}
              hint={t('crm.counts.suppressedHint')}
              isLoading={summary.isPending}
            />
            <StatCard
              icon={TriangleAlert}
              tone="danger"
              label={t('crm.counts.failed')}
              value={summary.data?.failed ?? 0}
              hint={t('crm.counts.failedHint')}
              isLoading={summary.isPending}
            />
          </div>

          <Card>
            <CardHeader
              title={t('crm.messages.title')}
              subtitle={
                data ? t('crm.messages.count', { count: data.pagination.totalItems }) : undefined
              }
            />

            <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
              <Select
                aria-label={t('crm.filters.branch')}
                options={branchOptions}
                value={branchId}
                onChange={(event) => onFilter(setBranchId)(event.target.value)}
              />
              <Select
                aria-label={t('crm.filters.purpose')}
                options={[
                  { value: '', label: t('crm.filters.allPurposes') },
                  ...Object.values(CommunicationPurpose).map((value) => ({
                    value,
                    label: t(`crm.purpose.${value}`),
                  })),
                ]}
                value={purpose}
                onChange={(event) =>
                  onFilter(setPurpose)(event.target.value as CommunicationPurpose | '')
                }
              />
              <Select
                aria-label={t('crm.filters.status')}
                options={[
                  { value: '', label: t('crm.filters.allStatuses') },
                  ...Object.values(CustomerCommunicationStatus).map((value) => ({
                    value,
                    label: t(`crm.status.${value}`),
                  })),
                ]}
                value={status}
                onChange={(event) =>
                  onFilter(setStatus)(event.target.value as CustomerCommunicationStatus | '')
                }
              />
              <Select
                aria-label={t('crm.filters.deliveryMode')}
                options={[
                  { value: '', label: t('crm.filters.allModes') },
                  ...Object.values(CommunicationDeliveryMode).map((value) => ({
                    value,
                    label: t(`crm.deliveryMode.${value}`),
                  })),
                ]}
                value={deliveryMode}
                onChange={(event) =>
                  onFilter(setDeliveryMode)(event.target.value as CommunicationDeliveryMode | '')
                }
              />
              <Input
                type="date"
                aria-label={t('crm.filters.dateFrom')}
                value={dateFrom}
                onChange={(event) => onFilter(setDateFrom)(event.target.value)}
              />
              <Input
                type="date"
                aria-label={t('crm.filters.dateTo')}
                value={dateTo}
                onChange={(event) => onFilter(setDateTo)(event.target.value)}
              />
            </div>

            <DataTable
              caption={t('crm.messages.tableCaption')}
              columns={columns}
              rows={data?.items}
              rowKey={(row) => row.id}
              isLoading={isPending}
              isError={isError}
              errorMessage={apiErrorMessage(error) ?? t('crm.messages.loadError')}
              onRetry={() => void refetch()}
              emptyIcon={MessageSquare}
              emptyTitle={t('crm.messages.empty')}
              emptyDescription={t('crm.messages.emptyHint')}
            />

            {data ? <Pagination pagination={data.pagination} onPageChange={setPage} /> : null}
          </Card>

          {/* A plain statement of what the strongest status here actually means. */}
          <Alert tone="info">{t('crm.messages.acceptedMeaning')}</Alert>
        </>
      ) : null}

      {tab === 'reminders' ? (
        <RemindersPanel canManage={can(PERMISSIONS.NOTIFICATIONS_SEND)} branches={branches} />
      ) : null}

      {tab === 'birthdays' ? <BirthdaysPanel /> : null}

      <CommunicationDetailDrawer communicationId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
